import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface SavedLocation {
  lat: number
  lon: number
  label: string
}

/** Stable key for a location (dedupe / home / remove), tolerant of float noise. */
export const locKey = (l: { lat: number; lon: number }) => `${l.lat.toFixed(4)},${l.lon.toFixed(4)}`

interface LocationState {
  /** The ACTIVE location — the single source that drives the whole app + globe marker. */
  saved: SavedLocation | null
  /** Saved locations the user switches between. Always contains `saved`. */
  list: SavedLocation[]
  /** Key of the location marked HOME / default. */
  homeKey: string | null
  /** Set true once we've attempted auto-geolocation, so we don't nag on every mount. */
  autoLocateTried: boolean
  /** Set the ACTIVE location (upserts into the saved list; the first one becomes home). */
  setSaved: (loc: SavedLocation) => void
  /** Remove a saved location; if it was active, fall back to home → first → none. */
  removeLocation: (key: string) => void
  /** Mark a saved location as home/default. */
  setHome: (key: string) => void
  setAutoLocateTried: (v: boolean) => void
  clear: () => void
}

// The store logic (shape + actions) is portable. Only persistence is platform-bound:
// `persist` defaults to localStorage on web; the RN port passes
// `storage: createJSONStorage(() => AsyncStorage)` — one line, no consumer changes.
export const useLocationStore = create<LocationState>()(
  persist(
    (set) => ({
      saved: null,
      list: [],
      homeKey: null,
      autoLocateTried: false,
      setSaved: (loc) =>
        set((s) => {
          const key = locKey(loc)
          const exists = s.list.some((l) => locKey(l) === key)
          const list = exists
            ? s.list.map((l) => (locKey(l) === key ? loc : l)) // refresh the label
            : [loc, ...s.list] // newest first
          return { saved: loc, list, homeKey: s.homeKey ?? key }
        }),
      removeLocation: (key) =>
        set((s) => {
          const list = s.list.filter((l) => locKey(l) !== key)
          const homeKey = s.homeKey === key ? (list[0] ? locKey(list[0]) : null) : s.homeKey
          let saved = s.saved
          if (saved && locKey(saved) === key) {
            saved = list.find((l) => locKey(l) === homeKey) ?? list[0] ?? null
          }
          return { list, homeKey, saved }
        }),
      setHome: (key) => set((s) => (s.list.some((l) => locKey(l) === key) ? { homeKey: key } : {})),
      setAutoLocateTried: (autoLocateTried) => set({ autoLocateTried }),
      clear: () => set({ saved: null, list: [], homeKey: null }),
    }),
    {
      name: 'taw.location',
      // Reconcile older persisted state (active-only, pre-list) into list + home so the
      // active location always appears in the saved list after an upgrade. Defensive
      // against malformed/corrupt persisted entries (never key off a bad coord).
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<LocationState>
        const valid = (l: unknown): l is SavedLocation =>
          !!l &&
          typeof l === 'object' &&
          Number.isFinite((l as SavedLocation).lat) &&
          Number.isFinite((l as SavedLocation).lon)
        const merged = { ...current, ...p }
        merged.list = (Array.isArray(p.list) ? p.list : []).filter(valid)
        if (merged.saved && !valid(merged.saved)) merged.saved = null
        if (merged.saved && !merged.list.some((l) => locKey(l) === locKey(merged.saved!))) {
          merged.list = [merged.saved, ...merged.list]
        }
        if (!merged.homeKey && merged.saved) merged.homeKey = locKey(merged.saved)
        return merged
      },
    },
  ),
)
